// frontend/src/components/admin/Activity.js
// Admin ▸ Activity (/admin/activity, audit.read): wireframes wf-admin-activity 1, 2, 4 and 5
// (YES-316), calls in docs/call-map.md section 20. Who used ADAS, when and for how long, read from
// the event log: counts, times and causes only. A teacher's uploads are never listed or opened here.
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { listRuns } from '../../services/runService';
import {
    PERIODS,
    periodQuery,
    getPeople,
    getEvents,
    getActions,
    formatDuration,
    formatHours,
    formatWhen,
    downloadCsv,
} from '../../services/activityService';
import { ROLE_LABELS } from '../../routes/landing';

const HEAD = { fontSize: 12, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', whiteSpace: 'nowrap' };

const ROLE_CHIP = {
    annotator: { bgcolor: 'grey.200', color: 'text.secondary' },
    researcher: { bgcolor: '#e8f1fb', color: 'primary.dark' },
    admin: { bgcolor: '#e8f1fb', color: 'primary.dark' },
    user: { bgcolor: '#e6f4ea', color: 'success.dark' },
};

export const RoleChip = ({ role }) => <Chip size="small" label={ROLE_LABELS[role] || role} sx={ROLE_CHIP[role]} />;

const Tile = ({ label, value, sub }) => (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 180 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{label}</Typography>
        <Typography variant="h5" component="p" sx={{ fontWeight: 700, mt: 0.5 }}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
);

export const Tiles = ({ children }) => <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>{children}</Box>;

export const Filter = ({ label, value, onChange, options, width = 180 }) => {
    const id = `activity-filter-${label.toLowerCase()}`;
    return (
    <FormControl size="small" sx={{ width }}>
        <InputLabel id={id}>{label}</InputLabel>
        <Select labelId={id} label={label} value={value} onChange={(e) => onChange(e.target.value)}>
            {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </Select>
    </FormControl>
    );
};

export const LoadFailed = ({ onRetry }) => (
    <Box sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom>Couldn't load activity</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
            Nothing was changed. The event log is kept even when this page can't show it.
        </Typography>
        <Button variant="contained" onClick={onRetry}>Try again</Button>
    </Box>
);

// Shown when the period starts before session events were recorded (frame 4)
export const BeforeSessions = ({ since, from }) => {
    if (since && from && new Date(from) >= new Date(since)) return null;
    const when = since ? new Date(since).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;
    return (
        <Alert severity="warning" icon={false} sx={{ mb: 2 }}>
            {when
                ? `Sessions are recorded from ${when}. Earlier activity shows coding counts only.`
                : 'No sessions have been recorded yet, so sessions and time signed in are blank. Coding counts are shown.'}
        </Alert>
    );
};

const dash = (v) => (v == null ? '—' : v);
const stamp = () => new Date().toISOString().slice(0, 10);

const ROLE_OPTIONS = [{ value: '', label: 'All roles' }, ...Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label }))];

const PeopleTab = ({ data }) => {
    const { totals, people, sessionsSince } = data;
    return (
        <>
            <Tiles>
                <Tile label="People active" value={totals.peopleActive} sub={`of ${totals.accounts} accounts`} />
                <Tile label="Sessions" value={dash(totals.sessions)} sub={totals.medianSessionMs != null ? `median ${formatDuration(totals.medianSessionMs)}` : null} />
                <Tile label="Time signed in" value={formatHours(totals.signedInMs)} sub="sum of session lengths" />
                <Tile label="Coding done" value={`${totals.lessonsSubmitted} lessons · ${totals.itemsDone} items`} sub="annotation · adjudication" />
            </Tiles>
            <BeforeSessions since={sessionsSince} from={data.from} />
            <Paper variant="outlined">
                {people.length === 0 ? (
                    <Box sx={{ p: 4 }}>
                        <Typography variant="h6" gutterBottom>No activity in this period</Typography>
                        <Typography color="text.secondary">Nobody signed in or coded between the dates picked. Try a longer period.</Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table aria-label="People">
                            <TableHead>
                                <TableRow>
                                    {['Name', 'Role', 'Sessions', 'Signed in', 'Lessons', 'Items', 'Items / hour', 'Last seen'].map((h) => (
                                        <TableCell key={h} sx={HEAD}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {people.map((p) => (
                                    <TableRow key={p.userId} hover>
                                        <TableCell sx={{ fontWeight: 500 }}>
                                            <Button component={RouterLink} to={`/admin/activity/${p.userId}`} sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 500 }}>
                                                {p.name}
                                            </Button>
                                        </TableCell>
                                        <TableCell><RoleChip role={p.role} /></TableCell>
                                        <TableCell>{dash(p.sessions)}</TableCell>
                                        <TableCell>{p.signedInMs == null ? '—' : formatDuration(p.signedInMs)}</TableCell>
                                        <TableCell>{p.lessonsSubmitted || '—'}</TableCell>
                                        <TableCell>{p.itemsDone || '—'}</TableCell>
                                        <TableCell>{dash(p.itemsPerHour)}</TableCell>
                                        <TableCell>{p.signedInNow ? 'Now' : formatWhen(p.lastSeenAt)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                Lessons: lesson sheets submitted (annotation). Items: adjudication items done. Both counted in the period, across the runs picked above.
            </Typography>
        </>
    );
};

const EventTable = ({ events }) => (
    <TableContainer>
        <Table aria-label="Event log" size="small">
            <TableHead>
                <TableRow>
                    {['Time', 'Person', 'Action', 'Run', 'On', 'Detail'].map((h) => <TableCell key={h} sx={HEAD}>{h}</TableCell>)}
                </TableRow>
            </TableHead>
            <TableBody>
                {events.map((e) => (
                    <TableRow key={e.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatWhen(e.at)}</TableCell>
                        <TableCell>{e.user ? e.user.name : <Typography variant="body2" color="text.secondary">Deleted account</Typography>}</TableCell>
                        <TableCell><Chip size="small" label={e.action} sx={{ fontFamily: 'monospace', bgcolor: 'grey.100' }} /></TableCell>
                        <TableCell>{e.run ? e.run.name : '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{dash(e.on)}</TableCell>
                        <TableCell>{dash(e.detail)}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

const peopleCsv = (people) => downloadCsv(
    `adas-activity-people-${stamp()}.csv`,
    ['name', 'role', 'sessions', 'signed_in_minutes', 'lessons_submitted', 'items_done', 'items_per_hour', 'last_seen'],
    people.map((p) => [p.name, p.role, p.sessions, p.signedInMs == null ? null : Math.round(p.signedInMs / 60000), p.lessonsSubmitted, p.itemsDone, p.itemsPerHour, p.lastSeenAt]),
);

const eventsCsv = (events) => downloadCsv(
    `adas-activity-events-${stamp()}.csv`,
    ['at', 'person', 'role', 'action', 'run', 'on', 'detail'],
    events.map((e) => [e.at, e.user?.name || 'Deleted account', e.user?.role, e.action, e.run?.name, e.on, e.detail]),
);

const Activity = () => {
    const [tab, setTab] = useState('people');
    const [period, setPeriod] = useState('7d');
    const [role, setRole] = useState('');
    const [runId, setRunId] = useState('');
    const [action, setAction] = useState('');
    const [person, setPerson] = useState('');
    const [runs, setRuns] = useState([]);
    const [actions, setActions] = useState([]);
    const [people, setPeople] = useState(null);
    const [log, setLog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [more, setMore] = useState(false);
    const [attempt, setAttempt] = useState(0);

    // Run and action choices; the page works without them
    useEffect(() => {
        listRuns().then((r) => setRuns(r || [])).catch(() => setRuns([]));
        getActions().then((a) => setActions(a || [])).catch(() => setActions([]));
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setFailed(false);
        const range = periodQuery(period);
        try {
            if (tab === 'people') {
                const data = await getPeople({ ...range, role, runId });
                setPeople({ ...data, from: range.from });
            } else {
                setLog(await getEvents({ ...range, runId, action, userId: person }));
                // The person filter lists everyone active in the period
                if (!people) setPeople({ ...(await getPeople(range)), from: range.from });
            }
        } catch (err) {
            setFailed(true);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, period, role, runId, action, person, attempt]);

    useEffect(() => { load(); }, [load]);

    const loadMore = async () => {
        setMore(true);
        try {
            const next = await getEvents({ ...periodQuery(period), runId, action, userId: person, before: log.next });
            setLog((current) => ({ events: [...current.events, ...next.events], next: next.next }));
        } catch (err) {
            setFailed(true);
        } finally {
            setMore(false);
        }
    };

    const runOptions = [{ value: '', label: 'All runs' }, ...runs.map((r) => ({ value: r._id || r.id, label: r.name }))];
    const actionOptions = [{ value: '', label: 'All actions' }, ...actions.map((a) => ({ value: a, label: a }))];
    const personOptions = [{ value: '', label: 'All people' }, ...((people?.people) || []).map((p) => ({ value: p.userId, label: p.name }))];

    const body = () => {
        if (failed) return <Paper variant="outlined"><LoadFailed onRetry={() => setAttempt((n) => n + 1)} /></Paper>;
        if (!(tab === 'people' ? people : log)) {
            return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
        }
        if (tab === 'people') return <PeopleTab data={people} />;
        return (
            <>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                    <Filter label="Action" value={action} onChange={setAction} options={actionOptions} width={220} />
                    <Filter label="Person" value={person} onChange={setPerson} options={personOptions} width={220} />
                </Box>
                <Paper variant="outlined">
                    {log.events.length === 0 ? (
                        <Box sx={{ p: 4 }}>
                            <Typography variant="h6" gutterBottom>No events</Typography>
                            <Typography color="text.secondary">Nothing matches these filters. Try a longer period or fewer filters.</Typography>
                        </Box>
                    ) : (
                        <>
                            <EventTable events={log.events} />
                            {log.next && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 1.5, borderTop: 1, borderColor: 'divider' }}>
                                    <Button onClick={loadMore} disabled={more}>{more ? 'Loading…' : 'Load 50 more'}</Button>
                                </Box>
                            )}
                        </>
                    )}
                </Paper>
            </>
        );
    };

    const canDownload = !loading && !failed && (tab === 'people' ? people?.people?.length : log?.events?.length);

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>Activity</Typography>
                <Typography variant="body2" color="text.secondary">
                    Who used ADAS, when and for how long. Counts and times only: no transcript text, codes, model output or uploads.
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                <Filter label="Period" value={period} onChange={setPeriod} options={PERIODS} width={170} />
                {tab === 'people' && <Filter label="Role" value={role} onChange={setRole} options={ROLE_OPTIONS} width={150} />}
                <Filter label="Run" value={runId} onChange={setRunId} options={runOptions} width={200} />
                <Box sx={{ flex: 1 }} />
                <ToggleButtonGroup size="small" exclusive value={tab} onChange={(e, v) => v && setTab(v)} aria-label="View">
                    <ToggleButton value="people">People</ToggleButton>
                    <ToggleButton value="log">Event log</ToggleButton>
                </ToggleButtonGroup>
                <Button
                    variant="outlined"
                    disabled={!canDownload}
                    onClick={() => (tab === 'people' ? peopleCsv(people.people) : eventsCsv(log.events))}
                >
                    Download CSV
                </Button>
            </Box>
            {body()}
        </Container>
    );
};

export default Activity;
